<template>
  <div class="flex flex-col gap-2" @click.stop @mousedown.stop>
    <div
      v-if="showBackButton && !showPropertySelection"
      class="flex items-center gap-2 cursor-pointer"
      @click="$emit('go-back')"
    >
      <ThemeImage
        :icon="imageNames.BACK_ARROW_WHITE"
        class="size-4"
        alt="go_back"
      />
      <span class="text-sm font-semibold text_color">Group</span>
    </div>

    <div v-if="!showPropertySelection">
      <div
        class="dropdown_item flex items-center !justify-between px-3 py-2 rounded-lg cursor-pointer"
        @click="openPropertySelection"
      >
        <span class="text-sm font-semibold text_color">Group by</span>
        <span class="flex items-center gap-2">
          <span class="text-sm secondary_color">{{
            selectedPropertyName || "None"
          }}</span>
          <ThemeImage
            :icon="imageNames.RIGHT_ARROW_SMALL_GREY"
            class="size-4"
            alt="go_right"
          />
        </span>
      </div>

      <div v-if="localGroupBy && groupOptions.length > 0">
        <div class="border_top my-2"></div>

        <div class="px-3 py-2">
          <span
            class="text-[11px] font-medium"
            style="color: var(--white-opacity-60)"
            >GROUPS</span
          >
        </div>

        <SkeletonLoader v-if="isLoadingOptions" page="manage_columns" />

        <div v-else class="max-h-[240px] overflow-y-auto">
          <VueDraggableNext
            v-model="groupOptions"
            item-key="id"
            handle=".drag_handle"
            ghost-class="ghost_item"
            class="flex flex-col gap-1"
          >
            <div
              v-for="option in groupOptions"
              :key="option.id"
              class="draggable_column_item flex items-center justify-between px-3 py-2 dropdown_item flex-shrink-0"
            >
              <div class="flex items-center gap-3 flex-1 min-w-0">
                <div
                  class="drag_handle hover:cursor-grab active:cursor-grabbing flex-shrink-0 opacity-60 hover:opacity-100 transition-opacity"
                >
                  <ThemeImage
                    :icon="imageNames.DRAGGABLE_DOTS"
                    alt="drag_handle"
                    class="size-4"
                  />
                </div>

                <span class="text-sm truncate text_color">
                  {{ option.value }}
                </span>
              </div>

              <div class="flex items-center gap-2 flex-shrink-0">
                <button @click.stop="toggleOptionVisibility(option.id)">
                  <ThemeImage
                    :icon="
                      option.visible
                        ? imageNames.EYE_GREY
                        : imageNames.EYE_OFF_GREY
                    "
                    :alt="option.visible ? 'visible' : 'hidden'"
                    class="size-4"
                  />
                </button>
              </div>
            </div>
          </VueDraggableNext>
        </div>
      </div>

      <div class="border_top my-2"></div>
      <div class="px-3 pb-1">
        <a-button
          class="primary_medium w-full"
          @click="$emit('done')"
        >
          Done
        </a-button>
      </div>
    </div>

    <div v-else class="flex flex-col gap-3">
      <div
        class="flex items-center gap-2 cursor-pointer"
        @click="closePropertySelection"
      >
        <ThemeImage
          :icon="imageNames.BACK_ARROW_WHITE"
          class="size-4"
          alt="go_back"
        />
        <span class="text-sm font-semibold text_color">Group by</span>
      </div>

      <div class="relative">
        <a-input
          ref="searchInputRef"
          v-model:value="searchQuery"
          class="input_medium"
          placeholder="Search properties"
          allow-clear
          autofocus
          @click.stop
          @mousedown.stop
        >
          <template #prefix>
            <ThemeImage
              :icon="imageNames.SEARCH_VARIANT1"
              alt="search_icon"
              class="size-4"
            />
          </template>
        </a-input>
      </div>

      <SkeletonLoader v-if="isLoading" page="manage_columns" />

      <template v-else-if="selectFields.length > 0">
        <div class="flex flex-col gap-1 max-h-[240px] overflow-y-auto">
          <div
            v-for="field in filteredFields"
            :key="field.id || field.slug"
            class="dropdown_item flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer flex-shrink-0"
            :class="{
              selected_option:
                localGroupBy ===
                (field.isPipelineGroupBy ? field.id : field.slug),
            }"
            @click="selectField(field)"
          >
            <ThemeImage
              :icon="getFieldIcon(field.type)"
              :alt="field.type"
              class="size-4"
            />
            <span class="text-sm">{{ field.name }}</span>
          </div>

          <div v-if="filteredFields.length === 0" class="w-full">
            <EmptyScreen
              source="group_by_search_empty"
              text="No properties found!"
              padding="my-6"
              css="size-16"
            />
          </div>
        </div>
      </template>

      <div v-else class="w-full px-2">
        <EmptyScreen
          source="group_by_empty"
          text="No select fields available"
          padding="my-6"
          css="size-16"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed, onMounted, nextTick } from "vue";
import { VueDraggableNext } from "vue-draggable-next";
import { useStore } from "vuex";
import { message } from "ant-design-vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import {
  customObjectAttributeTypes,
  EXCLUDED_GROUP_BY_FIELD_IDS,
} from "@/src/constants/crmConstants.js";
import CRMService from "@/src/api/CRMService.js";
import { entityTypes } from "@/src/constants/entityTypeConstants";
import { loadGroupByOptions } from "@/src/utils/viewHelpers.js";

const props = defineProps({
  groupBy: { type: String, default: null },
  entitySlug: { type: String, required: true },
  service: { type: String, default: null },
  showBackButton: { type: Boolean, default: false },
  projectId: { type: [String, Number], default: null },
  currentView: { type: Object, default: null },
});

const emit = defineEmits(["update:groupBy", "close", "go-back", "done"]);

const store = useStore();
const localGroupBy = ref(props.groupBy);
const searchQuery = ref("");
const showPropertySelection = ref(false);
const searchInputRef = ref(null);
const groupOptions = ref([]);
const isLoadingOptions = ref(false);
const selectedPropertyMeta = ref(null);

const isTickets = computed(() => props.entitySlug === entityTypes.TICKETS);
const isTasks = computed(() => props.entitySlug === entityTypes.TASKS);
const isDeal = computed(() => props.entitySlug === entityTypes.DEALS);

const isLoading = computed(() => {
  return store.getters["schema/isSchemaLoading"](
    props.entitySlug,
    isTasks.value ? props.projectId : null,
    props.service,
  );
});

const selectFields = computed(() => {
  const schemaFields =
    store.getters["schema/getSelectFieldsBySlug"](
      props.entitySlug,
      isTasks.value ? props.projectId : null,
      { excludeGroupBy: true },
      props.service,
    ) || [];

  // Filter out excluded fields (too many options or not supported for kanban view)
  const filteredSchemaFields = schemaFields.filter(
    (field) =>
      !EXCLUDED_GROUP_BY_FIELD_IDS.includes(field.id) &&
      !EXCLUDED_GROUP_BY_FIELD_IDS.includes(field.slug),
  );

  if (isDeal.value) {
    const pipelines = store.getters["pipelines/allPipelines"] || [];

    const pipelineFields = pipelines.map((pipeline) => ({
      id: `pipeline_${pipeline.id}`,
      slug: "pipeline_id",
      name: pipeline.name,
      type: "pipeline",
      isPipelineGroupBy: true,
      pipelineId: pipeline.id,
    }));

    return [...pipelineFields, ...filteredSchemaFields];
  }

  return filteredSchemaFields;
});

const filteredFields = computed(() => {
  if (!searchQuery.value.trim()) {
    return selectFields.value;
  }

  const query = searchQuery.value.toLowerCase().trim();
  return selectFields.value.filter(
    (field) =>
      field.name.toLowerCase().includes(query) ||
      field.slug.toLowerCase().includes(query),
  );
});

const selectedPropertyName = computed(() => {
  if (!localGroupBy.value) return null;

  if (selectedPropertyMeta.value) {
    return selectedPropertyMeta.value.name;
  }

  const field = selectFields.value.find(
    (f) => f.id === localGroupBy.value || f.slug === localGroupBy.value,
  );
  return field ? field.name : localGroupBy.value;
});

const getFieldIcon = (type) => {
  const attributeType = customObjectAttributeTypes.find(
    (attr) => attr.value === type,
  );
  return attributeType ? attributeType.icon : imageNames.PROPERTY_TYPE_TEXT;
};

const openPropertySelection = () => {
  showPropertySelection.value = true;
  nextTick(() => {
    if (searchInputRef.value) {
      searchInputRef.value.focus();
    }
  });
};

const closePropertySelection = () => {
  showPropertySelection.value = false;
  searchQuery.value = "";
};

const selectField = async (field) => {
  localGroupBy.value = field.isPipelineGroupBy ? field.id : field.slug;
  selectedPropertyMeta.value = field;

  emit("update:groupBy", field.slug);

  showPropertySelection.value = false;
  searchQuery.value = "";

  await loadGroupOptions(field);
};

const loadGroupOptions = async (field) => {
  try {
    isLoadingOptions.value = true;

    if (isTasks.value && !props.projectId) {
      message.error("Project ID is required for task operations");
      return;
    }

    groupOptions.value = await loadGroupByOptions(field, props.entitySlug, {
      currentView: props.currentView,
      projectId: props.projectId,
      preserveSavedConfig: true,
    });
  } catch (error) {
    console.error("Error loading group options:", error);
    message.error("Failed to load group options");
    groupOptions.value = [];
  } finally {
    isLoadingOptions.value = false;
  }
};

const toggleOptionVisibility = (optionId) => {
  const option = groupOptions.value.find((opt) => opt.id === optionId);
  if (option) {
    option.visible = !option.visible;
  }
};

const fetchSchema = async () => {
  const existingSchema = store.getters["schema/getSchemaBySlug"](
    props.entitySlug,
    isTasks.value ? props.projectId : null,
  );
  if (!existingSchema) {
    await store.dispatch("schema/fetchSchema", {
      slug: props.entitySlug,
      projectId: isTasks.value ? props.projectId : null,
    });
  }
};

const fetchPipelinesForDeals = async () => {
  if (isDeal.value) {
    const isPipelinesLoaded = store.getters["pipelines/isPipelinesLoaded"];
    if (!isPipelinesLoaded) {
      await store.dispatch("pipelines/fetchPipelines");
    }
  }
};

onMounted(async () => {
  await fetchSchema();
  await fetchPipelinesForDeals();

  if (localGroupBy.value) {
    // Try to find by unique id first (for pipelines), then by slug
    let field = selectFields.value.find(
      (f) => f.id === localGroupBy.value || f.slug === localGroupBy.value,
    );

    // Fallback: if groupBy is "pipeline_id" or "pipeline_stage_id", find the saved pipeline
    if (
      !field &&
      (localGroupBy.value === "pipeline_id" ||
        localGroupBy.value === "pipeline_stage_id") &&
      isDeal.value
    ) {
      const savedPipelineId = props.currentView?.group_by_config?.pipeline_id;

      if (savedPipelineId) {
        field = selectFields.value.find(
          (f) => f.isPipelineGroupBy && f.pipelineId === savedPipelineId,
        );
      } else {
        field = selectFields.value.find((f) => f.isPipelineGroupBy);
      }

      if (field && field.isPipelineGroupBy) {
        localGroupBy.value = field.id;
      }
    }

    if (field) {
      selectedPropertyMeta.value = field;
      await loadGroupOptions(field);
    }
  }
});

watch(
  () => props.groupBy,
  async (newVal) => {
    if (newVal) {
      // Try to find by unique id first (for pipelines), then by slug
      let field = selectFields.value.find(
        (f) => f.id === newVal || f.slug === newVal,
      );

      if (
        !field &&
        (newVal === "pipeline_id" || newVal === "pipeline_stage_id") &&
        isDeal.value
      ) {
        const savedPipelineId = props.currentView?.group_by_config?.pipeline_id;

        if (savedPipelineId) {
          field = selectFields.value.find(
            (f) => f.isPipelineGroupBy && f.pipelineId === savedPipelineId,
          );
        } else {
          field = selectFields.value.find((f) => f.isPipelineGroupBy);
        }

        // Update localGroupBy to use the unique id for the found pipeline
        if (field && field.isPipelineGroupBy) {
          localGroupBy.value = field.id;
        } else {
          localGroupBy.value = newVal;
        }
      } else {
        localGroupBy.value = newVal;
      }

      if (field) {
        selectedPropertyMeta.value = field;
        await loadGroupOptions(field);
      }
    } else {
      localGroupBy.value = null;
      groupOptions.value = [];
      selectedPropertyMeta.value = null;
    }
  },
);

watch(
  () => props.entitySlug,
  () => {
    fetchSchema();
  },
);

defineExpose({
  groupOptions,
  selectedPropertyMeta,
});
</script>

<style scoped>
.ghost_item {
  opacity: 0.5;
  background: var(--white-opacity-8);
}

.draggable_column_item {
  transition: all 0.2s ease;
}

.drag_handle {
  user-select: none;
}
</style>
